import { createCommandStepNode } from "~/components/build/Show/routes/steps/list/-components/TreeNode/CommandStepItem";
import { createWaitStepNode } from "~/components/build/Show/routes/steps/list/-components/TreeNode/WaitStepItem";
import { createTriggerStepNode } from "~/components/build/Show/routes/steps/list/-components/TreeNode/TriggerStepItem";
import { createInputStepNode } from "~/components/build/Show/routes/steps/list/-components/TreeNode/InputStepItem";
import { createStateHeaderNode } from "~/components/build/Show/routes/steps/list/-components/TreeNode/StateHeaderItem";
import { createSpacerNode } from "~/components/build/Show/routes/steps/list/-components/TreeNode/SpacerItem";
import { createGroupStepNode } from "~/components/build/Show/routes/steps/list/-components/TreeNode/GroupStepItem";
import { createMatrixStepNode } from "~/components/build/Show/routes/steps/list/-components/TreeNode/MatrixStepItem";
import { createParallelStepNode } from "~/components/build/Show/routes/steps/list/-components/TreeNode/ParallelStepItem";

import { Step, isMatrixCommandStep, isParallelCommandStep } from "~/lib/pipeline";
import { ReactNode } from "react";
import { Build } from "~/components/build/Show/lib/types";
import {
  FilterByOption,
  StateOrder,
  stepDirectlyMatchesFilters,
} from "~/components/build/Show/lib/useFilterStore";
import { CollapsibleState } from "~/components/build/Show/routes/steps/list/-components/TreeItem/CollapsibleState";

export { CollapsibleState } from "~/components/build/Show/routes/steps/list/-components/TreeItem/CollapsibleState";

export interface TreeItemContext {
  id: string;
  depth: number;
  collapsibleState: CollapsibleState;
  isLastChild: boolean;
}

export enum TreeNodeType {
  StepCommand = "step_command",
  StepGroup = "step_group",
  StepCommandMatrix = "step_command_matrix",
  StepCommandParallel = "step_command_parallel",
  StepInput = "step_input",
  StepTrigger = "step_trigger",
  StepWait = "step_wait",
  JobCommand = "job_command",
  ShowHiddenJobs = "show_hidden_jobs",
  StateHeader = "state_header",
  Spacer = "spacer",
}

export interface TreeNode {
  id: string;
  height: number;
  sticky?: boolean;
  ariaHidden?: boolean;
  deemphasized?: boolean;
  defaultCollapsibleState?: CollapsibleState;
  /** When set, overrides any stored or default collapsible state. The node cannot be toggled. */
  forcedCollapsibleState?: CollapsibleState;
  render: (context: TreeItemContext) => ReactNode;
  childNodes?: (collapsibleState: CollapsibleState, filters: FilterByOption[]) => TreeNode[];
}

export function createStepNode(step: Step, build: Build, filters: FilterByOption[] = []): TreeNode {
  switch (step.type) {
    case "group": {
      const node = createGroupStepNode(step, build);
      if (filters.length > 0) {
        const groupMatchesFilters = stepDirectlyMatchesFilters(step, filters, build);
        node.deemphasized = !groupMatchesFilters;
        if (!groupMatchesFilters) {
          node.defaultCollapsibleState = CollapsibleState.Expanded;
        }
      }
      return node;
    }
    case "command":
      if (isParallelCommandStep(step)) {
        return createParallelStepNode(step, build);
      } else if (isMatrixCommandStep(step)) {
        return createMatrixStepNode(step, build);
      }
      return createCommandStepNode(step);
    case "trigger":
      return createTriggerStepNode(step);
    case "wait":
      return createWaitStepNode(step);
    case "block":
    case "input":
      return createInputStepNode(step);
    default:
      throw new Error(`Unknown step type: ${step.type}`);
  }
}

/**
 * Turn a flat list of steps into a list of nodes with child groupings.
 */
export function createNodesForSteps(
  build: Build,
  steps: Step[],
  filters: FilterByOption[] = [],
): TreeNode[] {
  const nodes: TreeNode[] = [];

  steps.forEach((step) => {
    if (!step.groupUuid) {
      nodes.push(createStepNode(step, build, filters));
    }
  });

  return nodes;
}

/**
 * Create a tree of nodes grouped by state (ungrouped from parent)
 *
 * ie.
 *    **Failed**
 *      -> Rubocop
 *      -> Rspec
 *        -> Run 1
 *        -> Run 2
 */
export function createNodesForStepsGroupedByState(
  build: Build,
  stateCounts: Record<string, number>,
): TreeNode[] {
  const nodes: TreeNode[] = [];

  const statesWithCounts = StateOrder.filter((state) => stateCounts[state]);

  statesWithCounts.forEach((state, index) => {
    if (index > 0) {
      nodes.push(createSpacerNode(`${state}_spacer`));
    }

    const node = createStateHeaderNode(state, stateCounts[state], build);
    nodes.push(node);
  });

  return nodes;
}
